"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/app/_components/ui/dialog";
import { cn } from "@/app/_lib/utils";

/**
 * A dialog built ON TOP of shadcn's Dialog whose ✕ close uses the glass effect
 * (the stock shadcn close can't render the custom glass). Consumed by the Phase
 * 1 service modal. Do NOT fork app/_components/ui/dialog.tsx.
 */
const GlassDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
  }
>(({ className, children, hideClose = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // The slide-from/to-1/2 utilities look like a slide effect but aren't one here:
        // they cancel a tailwindcss-animate quirk where the zoom keyframe's "from" state
        // resets transform to translate(0,0), fighting the base -50%/-50% centering and
        // making the dialog visibly drift in from the top-left as it scales. Setting the
        // enter/exit translate to the same -50%/-50% keeps that translate constant across
        // the whole animation, so only scale + opacity actually animate. Do not remove.
        //
        // This outer box stays overflow-visible (default) so the desktop close button,
        // which floats above the box via -top-16, isn't clipped. The inner div below
        // owns the actual max-h + scroll so tall content doesn't run off-screen with no
        // way to reach it.
        "fixed left-[50%] top-[50%] z-50 w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-background shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-1/2 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-1/2",
        className,
      )}
      {...props}
    >
      <div className="max-h-[90dvh] overflow-y-auto rounded-[inherit]">
        {/* Mobile-only close button: lives inside the scroll container and
            stays `sticky` to its top, so it's always reachable regardless of
            how tall the content is or how the outer box's vertical centering
            pushes its top edge off-screen. Desktop keeps the floating button
            below (outside the scroll clip, so its -top-16 isn't cut off). */}
        {!hideClose && (
          <div className="sticky top-3 z-10 ml-auto w-fit pr-3 sm:hidden">
            <DialogPrimitive.Close
              aria-label="Close"
              className="liquid-glass flex h-10 w-10 items-center justify-center rounded-full text-white transition hover:scale-105 hover:text-white/70 focus:outline-none disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        )}
        {children}
      </div>
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label="Close"
          className="liquid-glass absolute right-3 top-3 z-10 hidden h-10 w-10 items-center justify-center rounded-full text-foreground/70 text-white transition hover:scale-105 hover:text-white/70 focus:outline-none disabled:pointer-events-none sm:-top-16 sm:right-0 sm:flex"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
GlassDialogContent.displayName = "GlassDialogContent";

export {
  Dialog as GlassDialog,
  DialogTrigger as GlassDialogTrigger,
  DialogClose as GlassDialogClose,
  DialogHeader as GlassDialogHeader,
  DialogTitle as GlassDialogTitle,
  DialogDescription as GlassDialogDescription,
  GlassDialogContent,
};
